import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { finesService, errorMessage } from '../../../services/finesService';
import { usePermissions } from '../../../hooks/usePermissions';
import { Tile, Notice } from './collectionsShared';
import { STATES, STATE_ORDER, counts, visibleRows, money, dubaiText, EXCEL_COLUMNS, excelRow, yeloLines, needsBranchEmail,
    needsCancelEmail, autoSummary, rowKey, amountText, intervalsText, contractTimes, contractComments } from './rentway.logic';
import Button from '../../../ui/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Badge from '../../../ui/Badge';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import EmptyState from '../../../ui/EmptyState';
import { Input, Checkbox, Switch } from '../../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellNumber, CellMuted } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { toast } from '../../../ui/feedback';

// Operations > Rentway (owner, 23 Oct 2026: "صفحة جديدة فى الاوبريشن اسمها rentway"): the booking sites' Dubai
// reservations as Rentway holds them (read by the engine every few hours, read-only) next to their Ejar bookings,
// with what is left to do on Ejar - book, cancel, or check dates / car / amount. Phase 2 (owner, 23 Oct 2026): "Book
// on Ejar" on a row books it on Ejar (the agent's Ejar login) and e-mails the branches, "Cancel on Ejar" cancels the
// booking of a reservation the customer cancelled + the cancellation e-mail - a preview first, then Confirm; the "Yelo"
// column shows what Yelo did. The Ejar comment is ready to copy. Phase 3 (owner, 23 Oct 2026): the page is "Brokers"
// and holds the Comfy site's bookings too (row.source "comfy" - the customer pays the counter, so its amount is the
// one to collect, not one paid); every call names the row's site.
const HEADERS = ['#', 'Reservation', 'Booked', 'Broker', 'Reference', 'Customer', 'Branch', 'Pick-up', 'Return', 'Days',
    'Car', 'Amount', 'Add-ons to collect', 'Site status', 'Ejar booking', 'Ejar contract', 'Contract opened',
    'Contract closed', 'Contract comments', 'Check', 'Yelo', 'Comment'];

const TONE_TEXT = { success: 'text-green-700', danger: 'text-red-700' };

// "Book on Ejar": the preview (read only - Ejar's checks and price next to Rentway's, the comment, the branch e-mail),
// then Confirm books it. Nothing is written on Ejar before Confirm.
function BookDialog({ row, onClose, onBooked }) {
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);
    const [test, setTest] = useState(false);
    const [checked, setChecked] = useState(false);
    const [booking, setBooking] = useState(false);
    const [done, setDone] = useState(null);
    const [tried, setTried] = useState(false);         // after a refusal / error: close and open again to retry
    // the phone a person typed when Ejar refused the booking site's (owner, 3 Nov 2026 - never guessed by the engine)
    const [phoneText, setPhoneText] = useState('');
    const [usedPhone, setUsedPhone] = useState('');
    useEffect(() => {
        let alive = true;
        setLoading(true); setErr('');
        finesService.rentwayPreview(row.number, false, row.source, usedPhone)
            .then((p) => {
                if (!alive) return;
                setPreview(p);
                if (p.last_typed_phone) setPhoneText((t) => t || p.last_typed_phone);   // typed on an earlier try
            })
            .catch((e) => {
                if (!alive) return;
                setErr(errorMessage(e));
                setPreview((old) => (old ? { ...old, can_book: false } : old));   // never booked on an old preview
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [row.number, row.source, usedPhone]);

    const book = async () => {
        setBooking(true); setErr('');
        try {
            const out = await finesService.rentwayBook(row.number, {
                test, version: preview?.version, checked: checked ? preview?.unsure_save?.id : undefined,
                source: row.source, phone: usedPhone,
            });
            setDone(out);
            toast.success(`Booked on Ejar: ${out.booking_number || 'the number is not read back yet'}`);
        } catch (e) { setErr(errorMessage(e)); setTried(true); }
        finally { setBooking(false); onBooked(); }
    };

    const p = preview || {};
    const canPress = p.can_book && !booking && !loading && !tried && (!p.unsure_save || checked);
    const priceTone = p.price_ok === false ? 'text-red-700' : p.price_ok ? 'text-green-700' : 'text-gray-700';
    const lines = [
        ['Broker', `${p.broker || row.broker_name || ''} · Ref ${p.reference || row.reference || ''}`],
        ['Customer', `${p.customer || row.customer || ''} · ${p.phone || 'phone not usable'}`],
        ['Car', `${p.group || row.group || ''} ${p.car || row.car || ''}${p.model ? ` → Ejar ${p.model}` : ''}`],
        ['Pick-up', `${dubaiText(p.pickup || row.date_out)} · ${p.branch || row.branch || ''}${p.flight ? ` · Flight ${p.flight}` : ''}`],
        ['Return', dubaiText(p.dropoff || row.date_in)],
        ['Add-ons to collect', p.cashier ? `${p.cashier_items} · AED ${money(p.cashier)}` : '—'],
        ['Books on Ejar as', p.ejar_login || 'no Ejar login chosen (Admin > Schedules)'],
    ];
    const email = p.email || {};
    return (
        <Dialog open onClose={onClose} size="xl" closeOnOverlay={false}
            title={`Book on Ejar · ${row.site || 'Rentway'} ${row.number}`}
            description="Read from Ejar now - nothing is written on Ejar until you press Book on Ejar now."
            footer={(
                <div className="flex w-full flex-wrap items-center justify-between gap-3">
                    <Checkbox label="Send the e-mail to me only (test) - the Ejar booking is real" checked={test}
                        disabled={booking || !!done} onChange={(e) => setTest(e.target.checked)} />
                    <div className="flex gap-2">
                        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
                        {!done && (
                            <Button variant="primary" onClick={book} disabled={!canPress}
                                leadingIcon={<Icon glyph={ICONS.check} size="sm" />}>
                                {booking ? 'Booking on Ejar…' : 'Book on Ejar now'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        >
            {loading ? <LoadingSpinner message="Reading Ejar's checks and price…" /> : (
                <div className="space-y-3 text-center">
                    {err && <Notice tone="danger">{err}{tried && !done ? ' · Close this and open it again to try again.' : ''}</Notice>}
                    {p.unsure_save && !done && (
                        <Notice tone="warning">
                            <div>
                                The last Save on Ejar{p.unsure_save.at ? ` (${dubaiText(p.unsure_save.at)})` : ''} answered an error
                                and may have gone through: {p.unsure_save.message}
                            </div>
                            <div className="mt-2 flex justify-center">
                                <Checkbox label={`I checked Ejar's Bookings: there is no booking for Ref ${p.reference || ''}`}
                                    checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                            </div>
                        </Notice>
                    )}
                    {done && (
                        <Notice tone="success">
                            Booked on Ejar: <b>{done.booking_number || 'the number is not read back yet'}</b> ({done.model}, AED {money(done.total)}) ·
                            {done.email?.sent ? ` e-mail sent to ${(done.email.to || []).join(', ')}` : ` the e-mail was NOT sent: ${done.email?.error || ''}`}
                        </Notice>
                    )}
                    {preview && !p.write_enabled && (
                        <Notice tone="warning">Booking on Ejar from this page is switched off on the server - this is the preview only.</Notice>
                    )}
                    {p.phone_box && !done && (
                        <Notice tone="warning">
                            <div>
                                {p.customer_refused
                                    ? `${p.customer_refused.why}${p.customer_refused.at ? ` (${dubaiText(p.customer_refused.at)})` : ''}`
                                    : `The ${row.site || 'Rentway'} phone ${p.site_phone || ''} cannot be used on Ejar.`}
                            </div>
                            <div>Check the customer&apos;s phone, then type it with its country code, a space, then the number: +971 52 946 6295.</div>
                            <div className="mt-2 flex flex-wrap items-end justify-center gap-2">
                                <div className="w-64">
                                    <Input aria-label="Phone for Ejar" placeholder="+971 52 946 6295" value={phoneText}
                                        onChange={(e) => setPhoneText(e.target.value)} />
                                </div>
                                <Button size="sm" variant="primary" onClick={() => setUsedPhone(phoneText.trim())}
                                    disabled={!phoneText.trim() || phoneText.trim() === usedPhone || booking}>
                                    Use this phone
                                </Button>
                            </div>
                            {usedPhone && <div className="mt-1">Ejar gets <b>{p.phone || '—'}</b> (typed {p.typed_phone || usedPhone}) · the branch e-mail shows it too.</div>}
                        </Notice>
                    )}
                    {(p.problems || []).length > 0 && (
                        <Notice tone="danger">Not ready to book: {(p.problems || []).join(' · ')}</Notice>
                    )}
                    {(p.checks || []).filter((c) => c.blocking).map((c) => <Notice key={c.message} tone="danger">Ejar: {c.message}</Notice>)}
                    {preview && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-lg border border-gray-300 p-3">
                                    <div className="text-xs font-bold uppercase text-gray-500">
                                        {p.amount_kind === 'counter' ? `${p.site || 'Comfy'} - to collect at the counter` : 'Rentway amount paid'}
                                    </div>
                                    <div className="text-2xl font-extrabold text-gray-900">AED {money(p.rentway_total)}</div>
                                </div>
                                <div className="rounded-lg border border-gray-300 p-3">
                                    <div className="text-xs font-bold uppercase text-gray-500">Ejar's price</div>
                                    <div className={`text-2xl font-extrabold ${priceTone}`}>
                                        {p.ejar_total == null ? '—' : `AED ${money(p.ejar_total)}`}
                                        {p.price_ok === false && ' · differs'}{p.price_ok && ' · same'}
                                    </div>
                                </div>
                            </div>
                            <TableContainer>
                                <Table className="min-w-full">
                                    <TableBody>
                                        {lines.map(([k, v]) => (
                                            <TableRow key={k} className="text-center">
                                                <TableCell className="whitespace-nowrap font-bold">{k}</TableCell>
                                                <TableCell>{v}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="text-center">
                                            <TableCell className="whitespace-nowrap font-bold">Ejar comment</TableCell>
                                            <TableCell><pre className="whitespace-pre-wrap font-sans text-sm">{p.comment}</pre></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className="rounded-lg border border-gray-300 p-3 text-sm">
                                <div className="font-bold">Branch e-mail · {email.subject}</div>
                                <div className="text-gray-600">
                                    To: {test ? 'you only (test)' : (email.to || []).join(', ')}
                                    {!test && (email.cc || []).length ? ` · CC: ${email.cc.join(', ')}` : ''}
                                </div>
                                <iframe title="Branch e-mail" sandbox="" srcDoc={email.html} className="mt-2 h-72 w-full rounded border border-gray-200 bg-white" />
                            </div>
                        </>
                    )}
                </div>
            )}
        </Dialog>
    );
}

// One booking site's account the engine signs in with (the admin only): kept ENCRYPTED on the server, never shown
// again. ``info``: that site's part of GET /ops/rentway/account.
function SiteAccount({ site, source, info, defaultUser, userLabel, onSaved }) {
    const [open, setOpen] = useState(!info.configured && !info.serverAccount);
    const [user, setUser] = useState(info.user || defaultUser);
    const [password, setPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            onSaved(await finesService.rentwayAccountSave(user.trim(), password, source));
            setPassword(''); setOpen(false); toast.success(`${site} account saved - press Read now to try it`);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-gray-700">
                    {site} account: {info.configured ? <b>{info.user}</b> : info.serverAccount ? 'the server settings' : <b className="text-red-700">none yet</b>}
                    {info.savedAt ? ` · saved ${info.savedAt.replace('T', ' ').slice(0, 16)}` : ''}
                    {info.refused && <b className="text-red-700">{` · ${site} refused this password - the reads wait for a new one`}</b>}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>{open ? 'Close' : 'Change'}</Button>
            </div>
            {open && (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div className="w-56"><Input aria-label={`${site} ${userLabel}`} placeholder={`${site} ${userLabel}`} value={user} onChange={(e) => setUser(e.target.value)} /></div>
                    <div className="w-56"><Input aria-label={`${site} password`} type="password" autoComplete="new-password" placeholder={`${site} password`}
                        value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                    <Button variant="primary" size="sm" onClick={save} disabled={saving || !user.trim() || !password}>Save</Button>
                    <span className="text-xs text-gray-500">Kept encrypted on the server; never shown again.</span>
                </div>
            )}
        </div>
    );
}

// The two booking sites' accounts: Rentway's (the station user) and Comfy's (Abdul Naseer's e-mail - owner, 23 Oct 2026).
function BrokerAccounts({ onSaved }) {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        finesService.rentwayAccount().then(setInfo).catch(() => setInfo(null));
    }, []);
    if (!info) return null;
    const saved = (a) => { setInfo(a); onSaved(); };
    return (
        <div className="space-y-2">
            <SiteAccount site="Rentway" source="rentway" info={info} defaultUser="YEL_UAE01" userLabel="user" onSaved={saved} />
            <SiteAccount site="Comfy" source="comfy" info={info.comfy || {}} defaultUser="a.naseer@iyelo.com" userLabel="e-mail" onSaved={saved} />
        </div>
    );
}

// "Cancel on Ejar" (the customer cancelled on the booking site): the preview (read only - Ejar's booking read now, the
// cancellation e-mail), then Confirm cancels it on Ejar. Nothing is written on Ejar before Confirm.
function CancelDialog({ row, onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);
    const [test, setTest] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [tried, setTried] = useState(false);
    useEffect(() => {
        let alive = true;
        finesService.rentwayCancelPreview(row.number, row.source)
            .then((p) => { if (alive) setPreview(p); })
            .catch((e) => { if (alive) setErr(errorMessage(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [row.number, row.source]);

    const cancelNow = async () => {
        setBusy(true); setErr('');
        try {
            const out = await finesService.rentwayCancel(row.number, { test, version: preview?.version, source: row.source });
            setDone(out);
            toast.success(`Cancelled on Ejar: ${out.booking_number}`);
        } catch (e) { setErr(errorMessage(e)); setTried(true); }
        finally { setBusy(false); onDone(); }
    };

    const p = preview || {};
    const email = p.email || {};
    const lines = [
        [row.site || 'Rentway', `${p.status_name || row.status_name || ''} · Ref ${p.reference || row.reference || ''} · ${p.broker || row.broker_name || ''}`],
        ['Ejar booking', `${p.booking_number || row.ejar?.booking_number || '—'}${p.ejar_status ? ` · ${p.ejar_status} on Ejar` : ''}`],
        ['Customer', p.customer || row.customer || ''],
        ['Car', p.car || row.car || ''],
        ['Pick-up', `${dubaiText(p.pickup || row.date_out)} · ${p.branch || row.branch || ''}`],
        ['Return', dubaiText(p.dropoff || row.date_in)],
        ['Amount on Ejar', p.amount != null ? `AED ${money(p.amount)}` : '—'],
        ['Cancel reason', p.reason || 'Canceled By System'],
        ['Cancels on Ejar as', p.ejar_login || 'no Ejar login chosen (Admin > Schedules)'],
    ];
    return (
        <Dialog open onClose={onClose} size="xl" closeOnOverlay={false}
            title={`Cancel on Ejar · ${row.site || 'Rentway'} ${row.number}`}
            description="Read from Ejar now - nothing is written on Ejar until you press Cancel on Ejar now."
            footer={(
                <div className="flex w-full flex-wrap items-center justify-between gap-3">
                    <Checkbox label="Send the e-mail to me only (test) - the Ejar cancel is real" checked={test}
                        disabled={busy || !!done} onChange={(e) => setTest(e.target.checked)} />
                    <div className="flex gap-2">
                        <Button onClick={onClose}>{done ? 'Close' : 'Back'}</Button>
                        {!done && (
                            <Button variant="danger" onClick={cancelNow} disabled={!p.can_cancel || busy || loading || tried}
                                leadingIcon={<Icon glyph={ICONS.close} size="sm" />}>
                                {busy ? 'Cancelling on Ejar…' : p.went_through ? 'Record it + send the e-mail' : 'Cancel on Ejar now'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        >
            {loading ? <LoadingSpinner message="Reading the booking on Ejar…" /> : (
                <div className="space-y-3 text-center">
                    {err && <Notice tone="danger">{err}{tried && !done ? ' · Close this and open it again to try again.' : ''}</Notice>}
                    {done && (
                        <Notice tone="success">
                            Cancelled on Ejar: <b>{done.booking_number}</b> ·
                            {done.email?.sent ? ` e-mail sent to ${(done.email.to || []).join(', ')}` : ` the e-mail was NOT sent: ${done.email?.error || ''}`}
                        </Notice>
                    )}
                    {preview && !p.write_enabled && (
                        <Notice tone="warning">Writing on Ejar from this page is switched off on the server - this is the preview only.</Notice>
                    )}
                    {(p.problems || []).length > 0 && <Notice tone="danger">Not ready to cancel: {p.problems.join(' · ')}</Notice>}
                    {p.went_through && !done && (
                        <Notice tone="warning">
                            Ejar shows this booking cancelled: the earlier Cancel went through after all. Pressing the button only
                            records it here and sends the cancellation e-mail - nothing is sent to Ejar again.
                        </Notice>
                    )}
                    {preview && (
                        <>
                            <TableContainer>
                                <Table className="min-w-full">
                                    <TableBody>
                                        {lines.map(([k, v]) => (
                                            <TableRow key={k} className="text-center">
                                                <TableCell className="whitespace-nowrap font-bold">{k}</TableCell>
                                                <TableCell>{v}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className="rounded-lg border border-gray-300 p-3 text-sm">
                                <div className="font-bold">Branch e-mail · {email.subject}</div>
                                <div className="text-gray-600">
                                    To: {test ? 'you only (test)' : (email.to || []).join(', ')}
                                    {!test && (email.cc || []).length ? ` · CC: ${email.cc.join(', ')}` : ''}
                                </div>
                                <iframe title="Cancellation e-mail" sandbox="" srcDoc={email.html} className="mt-2 h-72 w-full rounded border border-gray-200 bg-white" />
                            </div>
                        </>
                    )}
                </div>
            )}
        </Dialog>
    );
}

export default function RentwayPanel() {
    const { isAdmin, hasPermission } = usePermissions();
    const canBook = hasPermission('ops_rentway_book');
    const [booking, setBooking] = useState(null);
    const [cancelling, setCancelling] = useState(null);
    const [mailing, setMailing] = useState(null);
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    const load = useCallback(async (quiet = false) => {
        if (!quiet) { setLoading(true); setErr(''); }
        try { setData(await finesService.rentwayList()); } catch (e) { if (!quiet) setErr(errorMessage(e)); }
        finally { if (!quiet) setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const running = !!data?.status?.running;
    // while a read runs, follow it every 3 s (its stage and "x of y"); the rows come with it
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(() => load(true), 3000);
        return () => clearInterval(t);
    }, [running, load]);

    const readNow = async () => {
        setStarting(true);
        try { await finesService.rentwayRun(); toast.success('Reading Rentway + Comfy…'); await load(true); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setStarting(false); }
    };

    const rows = useMemo(() => data?.rows || [], [data]);
    const n = useMemo(() => counts(rows), [rows]);
    const list = useMemo(() => visibleRows(rows, filter, search), [rows, filter, search]);
    const st = data?.status || {};

    const exportExcel = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Brokers Booking Log');
        ws.columns = EXCEL_COLUMNS.map(([header, width]) => ({ header, width }));
        list.forEach((r) => ws.addRow(excelRow(r)));
        ws.getRow(1).font = { bold: true };
        ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD200' } };
        ws.views = [{ state: 'frozen', ySplit: 1 }];
        ws.eachRow((row, i) => {
            row.alignment = { horizontal: 'center', vertical: 'middle', wrapText: i > 1 };
            if (i > 1) {
                [1, 8, 10].forEach((c) => { row.getCell(c).numFmt = 'dd/mm/yyyy'; });
                [12, 13, 22].forEach((c) => { row.getCell(c).numFmt = '#,##0.00'; });
            }
        });
        const day = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
        saveAs(new Blob([await wb.xlsx.writeBuffer()]), `Brokers_Booking_Log_${day}.xlsx`);
    };

    const [switching, setSwitching] = useState(false);
    const toggleAuto = async (on) => {
        setSwitching(true);
        try {
            const out = await finesService.rentwayAuto(on);
            toast.success(out.auto ? 'Automatic booking / cancelling on Ejar is ON' : 'Automatic booking / cancelling on Ejar is OFF');
            await load(true);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSwitching(false); }
    };

    const sendBranchEmail = async (r, kind = 'new') => {
        setMailing(`${rowKey(r)}:${kind}`);
        try {
            const out = await finesService.rentwayEmail(r.number, false, kind, r.source);
            if (out.sent) toast.success(`E-mail sent to ${(out.to || []).join(', ')}`);
            else toast.error(`The e-mail was not sent: ${out.error || ''}`);
            await load(true);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setMailing(null); }
    };

    const copyComment = async (text) => {
        try { await navigator.clipboard.writeText(text); toast.success('Comment copied'); }
        catch (e) { toast.error('Copy failed - select the text by hand'); }
    };

    if (loading && !data) return <LoadingSpinner message="Loading the broker bookings…" />;
    const readEvery = intervalsText(st);
    const agents = [['Rentway', st.last_result?.agent], ['Comfy', st.last_result?.comfy?.agent]]
        .map(([site, a]) => (autoSummary(a) ? `${site}: ${autoSummary(a)}` : '')).filter(Boolean);

    return (
        <div className="space-y-4">
            <Card
                title="Broker bookings"
                subtitle={`Booking sites' Dubai reservations (Rentway + Comfy) checked against Ejar · ${readEvery}`
                    + `${st.last_ok ? ` · last read ${st.last_ok.slice(0, 16)}` : ''}${st.next_run && !running ? ` · next ${st.next_run.slice(11, 16)}` : ''}`}
                actions={(
                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" size="sm" onClick={exportExcel} disabled={!list.length}
                            leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>Excel</Button>
                        <Button variant="primary" size="sm" onClick={readNow} disabled={running || starting || !st.configured}
                            leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>{running ? 'Reading…' : 'Read now'}</Button>
                    </div>
                )}
            >
                <div className="space-y-3">
                    {err && <Notice tone="danger">{err}</Notice>}
                    {data && !st.configured && (
                        <Notice tone="warning">No Rentway or Comfy account yet: the reservations are not read.
                            {isAdmin ? ' Save them below.' : ' The admin saves them on this page.'}</Notice>
                    )}
                    {isAdmin && <BrokerAccounts onSaved={() => load(true)} />}
                    {canBook && data?.booking && (
                        <div className="text-xs text-gray-600">
                            Book on Ejar: {data.booking.enabled ? <b className="text-green-700">on</b> : <b className="text-amber-700">switched off on the server (preview only)</b>}
                            {' · '}books as {data.booking.login ? <b>{data.booking.login}</b> : <b className="text-red-700">no Ejar login chosen (Admin &gt; Schedules)</b>}
                        </div>
                    )}
                    {data?.booking && (isAdmin || canBook) && (
                        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                            <Switch checked={!!data.booking.auto}
                                disabled={!isAdmin || switching || (!data.booking.enabled && !data.booking.auto)}
                                onChange={toggleAuto}
                                label={`Automatic: ${data.booking.auto ? 'ON' : 'OFF'}`}
                                description={`After every read (${readEvery}) Yelo books the new reservations and cancels the cancelled ones on Ejar by itself, with the branch e-mails`} />
                            {agents.map((t) => <span key={t} className="text-xs text-gray-600">{t}</span>)}
                        </div>
                    )}
                    {st.last_error && !running && <Notice tone="danger">The last read failed ({st.last_run}): {st.last_error}</Notice>}
                    {running && (
                        <ProgressBar done={st.progress?.done || 0} total={st.progress?.total || 0} unit="reservations"
                            state="running" label={st.progress?.stage || 'Reading Rentway + Comfy'} />
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Tile id="action" label="Needs action on Ejar" value={n.action} tone={n.action ? 'text-red-700' : 'text-green-700'}
                            active={filter === 'action'} onPick={setFilter} />
                        {STATE_ORDER.map((s) => (
                            <Tile key={s} id={s} label={STATES[s][0]} value={n[s] || 0} active={filter === s} onPick={setFilter} />
                        ))}
                        <Tile id="all" label="All reservations" value={n.all} active={filter === 'all'} onPick={setFilter} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full max-w-sm">
                            <Input aria-label="Search" placeholder="Search name, reference, reservation, Ejar number…"
                                value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <span className="text-xs text-gray-500">
                            {list.length} of {rows.length} reservations{data?.tests ? ` · ${data.tests} test reservations left out` : ''}
                        </span>
                    </div>
                </div>
            </Card>

            {!rows.length ? (
                <EmptyState title="No reservations yet" description={st.configured ? 'Press Read now to read Rentway and Comfy.' : 'Waiting for the Rentway / Comfy account on the server.'} />
            ) : (
                <TableContainer maxHeight="70vh" className="overflow-auto">
                    <Table sticky className="min-w-full">
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((h) => <TableHeader key={h} className="whitespace-nowrap text-center">{h}</TableHeader>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((r, i) => {
                                const [label, tone] = STATES[r.state] || [r.state, 'neutral'];
                                const ejar = r.ejar || {};
                                const contract = contractTimes(ejar);
                                const said = contractComments(ejar);
                                return (
                                    <TableRow key={rowKey(r)} className="text-center">
                                        <TableCell><CellNumber value={i + 1} /></TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <CellId>{String(r.number)}</CellId><CellMuted>{r.site || 'Rentway'}</CellMuted>
                                            {r.versions > 1 && <CellMuted>{`changed · v${r.version}`}</CellMuted>}
                                        </TableCell>
                                        <TableCell><span className="ds-cell-date">{dubaiText(r.created_at)}</span></TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <Badge variant={r.broker_known ? 'info' : 'warning'} size="sm">{r.broker_name || r.broker}</Badge>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap"><CellText>{r.reference}</CellText></TableCell>
                                        <TableCell><CellText>{r.customer}</CellText><CellMuted>{r.phone}</CellMuted></TableCell>
                                        <TableCell><CellText>{r.branch}</CellText></TableCell>
                                        <TableCell><span className="ds-cell-date">{dubaiText(r.date_out)}</span>{r.flight && <CellMuted>{r.flight}</CellMuted>}</TableCell>
                                        <TableCell><span className="ds-cell-date">{dubaiText(r.date_in)}</span></TableCell>
                                        <TableCell className="ds-cell-num">{r.days}</TableCell>
                                        <TableCell className="whitespace-nowrap"><CellText>{r.group}</CellText><CellMuted>{r.car}</CellMuted></TableCell>
                                        <TableCell className="whitespace-nowrap"><span className="ds-cell-num">{money(r.invoice)}</span><CellMuted>{amountText(r)}</CellMuted></TableCell>
                                        <TableCell>
                                            {r.cashier ? <><span className="ds-cell-num">{money(r.cashier)}</span><CellMuted>{r.cashier_items}</CellMuted></> : ''}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap"><CellText>{r.status_name}</CellText></TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {ejar.booking_number ? <><CellText>{ejar.booking_number}</CellText><CellMuted>{`${ejar.status || ''} · ${money(ejar.amount)}`}</CellMuted></> : <CellMuted>—</CellMuted>}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">{ejar.contract_no ? <CellId>{ejar.contract_no}</CellId> : ''}</TableCell>
                                        <TableCell className="whitespace-nowrap"><span className="ds-cell-date">{contract.opened}</span></TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {contract.closed === 'Open'
                                                ? <span className="font-bold text-green-700">Open</span>
                                                : <span className="ds-cell-date">{contract.closed}</span>}
                                        </TableCell>
                                        <TableCell className="min-w-[300px] max-w-[420px]">
                                            {said.list.map((c) => (
                                                <div key={c.key} className="mb-1 last:mb-0">
                                                    <CellMuted>{`${c.day} ${c.time}${c.by ? ` · ${c.by}` : ''}`}</CellMuted>
                                                    <div className="whitespace-pre-wrap break-words text-sm text-gray-900">{c.text}</div>
                                                </div>
                                            ))}
                                            {ejar.contract_no && !said.list.length && (
                                                <CellMuted>{said.notRead ? 'Not read from Ejar yet' : 'No comments'}</CellMuted>
                                            )}
                                            {said.read && <CellMuted>{`read ${said.read}`}</CellMuted>}
                                        </TableCell>
                                        <TableCell className="min-w-[180px]">
                                            <StatusBadge status={r.state} tone={tone} label={label} />
                                            {(r.reasons || []).map((why) => <CellMuted key={why}>{why}</CellMuted>)}
                                            {canBook && r.state === 'book' && (
                                                <div className="mt-1">
                                                    <Button variant="primary" size="sm" onClick={() => setBooking(r)}
                                                        leadingIcon={<Icon glyph={ICONS.calendar} size="sm" />}>Book on Ejar</Button>
                                                </div>
                                            )}
                                            {canBook && (r.state === 'cancel' || r.yelo?.cancel?.outcome === 'failed') && (
                                                <div className="mt-1">
                                                    <Button variant="danger" size="sm" onClick={() => setCancelling(r)}
                                                        leadingIcon={<Icon glyph={ICONS.close} size="sm" />}>Cancel on Ejar</Button>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="min-w-[200px]">
                                            {yeloLines(r).map(([text, t]) => (
                                                <div key={text} className={`text-xs font-semibold ${TONE_TEXT[t] || ''}`}>{text}</div>
                                            ))}
                                            {canBook && needsBranchEmail(r) && (
                                                <div className="mt-1">
                                                    <Button variant="secondary" size="sm" onClick={() => sendBranchEmail(r)}
                                                        disabled={mailing === `${rowKey(r)}:new`}>
                                                        {mailing === `${rowKey(r)}:new` ? 'Sending…' : 'Send the e-mail to the branches'}
                                                    </Button>
                                                </div>
                                            )}
                                            {canBook && needsCancelEmail(r) && (
                                                <div className="mt-1">
                                                    <Button variant="secondary" size="sm" onClick={() => sendBranchEmail(r, 'cancel')}
                                                        disabled={mailing === `${rowKey(r)}:cancel`}>
                                                        {mailing === `${rowKey(r)}:cancel` ? 'Sending…' : 'Send the cancellation e-mail to the branches'}
                                                    </Button>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button variant="ghost" size="sm" onClick={() => copyComment(r.comment)} title={r.comment}
                                                leadingIcon={<Icon glyph={ICONS.copy} size="sm" />}>Copy</Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {booking && <BookDialog row={booking} onClose={() => setBooking(null)} onBooked={() => load(true)} />}
            {cancelling && <CancelDialog row={cancelling} onClose={() => setCancelling(null)} onDone={() => load(true)} />}
        </div>
    );
}
